import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

interface SignInForm {
  id: string;
  password: string;
}

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SignInForm>({
    id: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post('http://localhost:5000/api/auth/signin', formData);

      if (response.data.success) {
        // Save token to localStorage
        localStorage.setItem('token', response.data.data.token);
        
        // Fetch complete user profile data
        const userResponse = await axios.get('http://localhost:5000/api/users/profile', {
          headers: {
            Authorization: `Bearer ${response.data.data.token}`
          }
        });

        if (userResponse.data.success) {
          // Save complete user data to localStorage
          localStorage.setItem('user', JSON.stringify(userResponse.data.data.user));
          
          // Navigate to dashboard
          navigate('/dashboard');
        } else {
          setError('Error fetching user profile');
        }
      } else {
        setError(response.data.message || 'Sign in failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleRememberMeChange = () => {
    setRememberMe(!rememberMe);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation - Matches Welcome.tsx navbar */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          <div className="navbar-actions">
            <button
              onClick={() => navigate('/')}
              className="btn btn-ghost"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/signup')}
              className="btn btn-primary"
            >
              Sign up
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content - Takes full remaining height and centers content */}
      <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-8 px-4">
        <div className="w-full max-w-md">
          <div className="auth-card fade-in">
            <div className="auth-header">
              <div className="auth-logo">NS</div>
              <h1 className="auth-title">Sign In to NexSync</h1>
              <p className="auth-subtitle">
                Or <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-medium">sign up</Link> for an account
              </p>
            </div>

            {error && (
              <div className="error-message mb-6">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              </div>
            )}

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="space-y-6">
                <div className="form-group">
                  <label htmlFor="id" className="form-label">
                    User ID
                  </label>
                  <input
                    id="id"
                    name="id"
                    type="text"
                    required
                    value={formData.id}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Enter your user ID (e.g., FRE1234560001)"
                    disabled={isLoading}
                  />
                  <div className="form-hint">
                    Your unique NexSync identifier
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="password" className="form-label">
                    Password
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter your password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={togglePasswordVisibility}
                      disabled={isLoading}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-options mt-6">
                <label className="remember-me">
                  <input 
                    type="checkbox" 
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    disabled={isLoading}
                    className="mr-2"
                  />
                  Remember me for 30 days
                </label>
                <button 
                  type="button" 
                  className="forgot-password"
                  disabled={isLoading}
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary btn-full mt-8"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="loading-spinner small"></div>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="auth-footer mt-8">
              <button
                onClick={() => navigate('/')}
                className="back-to-home"
                disabled={isLoading}
              >
                ← Back to Home
              </button>
            </div>

            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Need help signing in?{' '}
                  <button 
                    className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                    onClick={() => navigate('/contactus')}
                  >
                    Contact Support
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;